/**
 * Common API Service for making HTTP requests.
 *
 * Handles:
 *  - Automatic Authorization header using the stored access token
 *  - Automatic refresh-token flow when an access token is rejected (401)
 *  - Separate storage for the short-lived "verification token" used only
 *    during the mobile/email OTP -> signup flow (this is NOT the session
 *    access token, so it is never auto-attached — callers pass it explicitly)
 */

import { videoCreatorEndPoint } from "../Constants/constants";

/* ===================== TOKEN STORAGE ===================== */

const VERIFICATION_TOKEN_KEY = "verificationToken"; // short-lived, OTP -> signup flow only
const ACCESS_TOKEN_KEY = "accessToken"; // real session token
const REFRESH_TOKEN_KEY = "refreshToken";

// --- verification token (OTP flow only) ---
export const getVerificationToken = () =>
  localStorage.getItem(VERIFICATION_TOKEN_KEY);

export const setVerificationToken = (token) => {
  if (token) localStorage.setItem(VERIFICATION_TOKEN_KEY, token);
};

export const clearVerificationToken = () =>
  localStorage.removeItem(VERIFICATION_TOKEN_KEY);

// --- session tokens (set once signup/login actually succeeds) ---
export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = ({ accessToken, refreshToken } = {}) => {
  if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Lets the rest of the app react when the session is fully dead
// (refresh token itself expired) — e.g. redirect to login.
let unauthorizedHandler = null;
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = fn;
};
const notifyUnauthorized = () => {
  if (unauthorizedHandler) unauthorizedHandler();
};

const handleSessionExpired = () => {
  clearTokens();
  notifyUnauthorized();
};

/* ===================== INTERNAL HELPERS ===================== */

const buildOptions = (method, data, headers, token) => {
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers, // explicit headers passed by the caller always win
    },
  };

  if (data && (method === "POST" || method === "PUT" || method === "PATCH")) {
    options.body = JSON.stringify(data);
  }

  return options;
};

let isRefreshing = false;
let refreshPromise = null;

const refreshAccessToken = async () => {
  // If a refresh is already in progress, everyone else piggybacks on it
  // instead of firing duplicate refresh calls.
  if (isRefreshing) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await fetch(`${videoCreatorEndPoint}auth/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.alert || result?.message || "Session expired");
      }

      // NOTE: adjust these field names to match what your
      // auth/refresh-token endpoint actually returns.
      const newAccessToken = result?.data?.accessToken;
      const newRefreshToken = result?.data?.refreshToken;

      setTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken });
      return newAccessToken;
    } catch (err) {
      handleSessionExpired();
      throw err;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/* ===================== PUBLIC API ===================== */

/**
 * Common API Service for making HTTP requests
 * @param {string} url - API endpoint
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {object} data - Request data (for POST, PUT, etc.)
 * @param {object} headers - Custom headers (overrides the auto-attached Authorization header)
 * @param {boolean} _isRetry - internal flag used for the one-time refresh retry, do not pass manually
 * @returns {Promise<object>} - API response data
 */
export const callApi = async (
  url,
  method = "GET",
  data = null,
  headers = {},
  _isRetry = false,
) => {
  try {
    const token = getAccessToken();
    const options = buildOptions(method, data, headers, token);

    const response = await fetch(url, options);
    const result = await response.json();

    if (!response.ok) {
      // Access token rejected -> try refreshing it once, then retry the original call.
      if (response.status === 401 && !_isRetry) {
        try {
          if (!getRefreshToken()) {
            handleSessionExpired();
            throw new Error("Session expired. Please log in again.");
          }

          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            return callApi(url, method, data, headers, true);
          }
        } catch {
          handleSessionExpired();
          throw new Error("Session expired. Please log in again.");
        }
      }

      // Use the server's alert/message, fallback if missing.
      throw new Error(result?.alert || result?.message || "Something went wrong");
    }

    return result;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};